import React, { useId } from 'react';
import { LucideProps } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Gemini (Google) mark: the four-point spark with its brand gradient.
 * Path and gradient from `@lobehub/icons` (MIT). The gradient id is per
 * instance (`useId`) so two marks on one page don't share a `<defs>` entry.
 */
export const Gemini = React.forwardRef<SVGSVGElement, LucideProps>(
  ({ size = 24, className, 'aria-label': ariaLabel, absoluteStrokeWidth: _absoluteStrokeWidth, ...props }, ref) => {
    const gradientId = useId();
    return (
      <svg
        ref={ref}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        // Decorative by default: it sits beside its visible name. Pass
        // `aria-label` when the mark stands alone and should be announced.
        role={ariaLabel ? 'img' : undefined}
        aria-label={ariaLabel}
        aria-hidden={ariaLabel ? undefined : true}
        className={cn('shrink-0', className)}
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" x2="68.73%" y1="100%" y2="30.395%">
            <stop offset="0%" stopColor="#1C7DFF" />
            <stop offset="52.021%" stopColor="#1C69FF" />
            <stop offset="100%" stopColor="#F0DCD6" />
          </linearGradient>
        </defs>
        <path
          d="M12 24A14.304 14.304 0 000 12 14.304 14.304 0 0012 0a14.305 14.305 0 0012 12 14.305 14.305 0 00-12 12"
          fill={`url(#${gradientId})`}
        />
      </svg>
    );
  },
);

Gemini.displayName = 'Gemini';
